import React, { useState, useEffect } from 'react';
import { useIsMounted } from '@/hooks/useIsMounted';
import OrderSectionHeader from './OrderSectionHeader';
import { formatNumber, formatSummaryAmount } from '@/utils/formatters';
import styles from './OrderManagement.module.css';
import { Note, Placeholder } from '@/components/ui';

/** Normalised summary shape consumed by the base component. */
export interface CompletedSummary {
  totalCount: number;
  totalQty: number;
  totalAmt: string | number;
  // null when the page mixes domestic and global orders — totalAmt then spans currencies
  // and is rendered without a symbol.
  currency?: string | null;
}

type Side = 'buy' | 'sell';

export interface BaseCompletedOrdersProps<TBuyData, TSellData> {
  /** Prefix for section titles, e.g. "ETF" or "" */
  titlePrefix?: string;
  /**
   * Fetch buy orders and return both normalised summary data and the raw
   * domain data that will be forwarded to renderBuySubSections.
   */
  fetchBuyOrders: () => Promise<{ summary: CompletedSummary; data: TBuyData }>;
  /** Same as fetchBuyOrders but for the sell side. */
  fetchSellOrders: () => Promise<{ summary: CompletedSummary; data: TSellData }>;
  /** Render the sub-section tables for the buy side. */
  renderBuySubSections: (data: TBuyData) => React.ReactNode;
  /** Render the sub-section tables for the sell side. */
  renderSellSubSections: (data: TSellData) => React.ReactNode;
  /** True when there is nothing to display for the buy side. */
  isBuyEmpty: (data: TBuyData) => boolean;
  /** True when there is nothing to display for the sell side. */
  isSellEmpty: (data: TSellData) => boolean;
}

function BaseCompletedOrders<TBuyData, TSellData>({
  titlePrefix = '',
  fetchBuyOrders,
  fetchSellOrders,
  renderBuySubSections,
  renderSellSubSections,
  isBuyEmpty,
  isSellEmpty,
}: BaseCompletedOrdersProps<TBuyData, TSellData>): React.ReactElement {
  const [buySummary, setBuySummary] = useState<CompletedSummary | null>(null);
  const [sellSummary, setSellSummary] = useState<CompletedSummary | null>(null);
  const [buyData, setBuyData] = useState<TBuyData | null>(null);
  const [sellData, setSellData] = useState<TSellData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useIsMounted();

  const prefix = titlePrefix ? `${titlePrefix} ` : '';

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [buyRes, sellRes] = await Promise.all([fetchBuyOrders(), fetchSellOrders()]);
        if (!isMounted.current) return;
        setBuySummary(buyRes.summary);
        setBuyData(buyRes.data);
        setSellSummary(sellRes.summary);
        setSellData(sellRes.data);
      } catch {
        if (isMounted.current) setError(`Failed to load ${prefix}completed orders`);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderSummaryChips = (summary: CompletedSummary) => (
    <div className={styles['summary-row']}>
      <div className={styles['summary-chip']}>
        <span className={styles['summary-chip-label']}>Total Orders</span>
        <span className={styles['summary-chip-value']}>{formatNumber(summary.totalCount, 0)}</span>
      </div>
      <div className={styles['summary-chip']}>
        <span className={styles['summary-chip-label']}>Total Quantity</span>
        <span className={styles['summary-chip-value']}>{formatNumber(summary.totalQty, 0)}</span>
      </div>
      <div className={styles['summary-chip']}>
        <span className={styles['summary-chip-label']}>Total Amount</span>
        <span className={styles['summary-chip-value']}>
          {formatSummaryAmount(summary.totalAmt, summary.currency)}
        </span>
      </div>
    </div>
  );

  const renderSection = (side: Side) => {
    const summary = side === 'buy' ? buySummary : sellSummary;
    const data = side === 'buy' ? buyData : sellData;
    const label = side === 'buy' ? 'Buy' : 'Sell';
    const empty = data === null
      || (side === 'buy' ? isBuyEmpty(data as TBuyData) : isSellEmpty(data as TSellData));

    return (
      <div className={styles['order-section']}>
        <OrderSectionHeader side={side} title={`Completed ${prefix}${label} Orders`} />

        {summary && renderSummaryChips(summary)}

        {empty ? (
          <div className={styles.empty}>
            <Placeholder message={<>No completed {prefix.toLowerCase()}{label.toLowerCase()} orders found.</>} />
          </div>
        ) : (
          <>
            {side === 'buy'
              ? renderBuySubSections(data as TBuyData)
              : renderSellSubSections(data as TSellData)}
          </>
        )}
      </div>
    );
  };

  if (loading) return <Placeholder kind="loading" />;

  return (
    <div>
      {error && <div className={styles.messages}><Note tone="error">{error}</Note></div>}
      {renderSection('buy')}
      {renderSection('sell')}
    </div>
  );
}

export default BaseCompletedOrders;
